import { useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import {
  CircleDot, Moon, CheckCircle2, Mail, MessageSquare, PenLine,
  Check, Clock, Pencil, X as XIcon, CalendarPlus,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { Followup, FollowupSource } from '../types'
import { AnimatedChevron, DataRow } from '@tendbot/ui'
import { expandCollapse } from '@tendbot/ui'
import { relativeTime } from '@tendbot/ui'
import SnoozeOptions from './SnoozeOptions'

// Format an ISO datetime into the YYYY-MM-DD value that <input type="date"> expects.
function isoToInputDate(iso: string): string {
  const d = new Date(iso)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Convert YYYY-MM-DD (local date from <input type="date">) to ISO UTC for the backend.
function inputDateToIso(input: string): string {
  return new Date(`${input}T00:00:00`).toISOString()
}

// ─── Urgency metadata ─────────────────────────────────────────
interface UrgencyMeta {
  borderColor: string
  iconBg: string
  textColor: string
  statusIcon: LucideIcon
}

function getUrgencyMeta(loop: Followup): UrgencyMeta {
  if (loop.status === 'resolved') {
    return {
      borderColor: 'border-l-emerald',
      iconBg: 'bg-emerald/10',
      textColor: 'text-emerald',
      statusIcon: CheckCircle2,
    }
  }
  if (loop.status === 'snoozed') {
    return {
      borderColor: 'border-l-indigo-400',
      iconBg: 'bg-indigo-400/10',
      textColor: 'text-indigo-400',
      statusIcon: Moon,
    }
  }

  // Open — urgency by due date
  if (!loop.dueAt) {
    return {
      borderColor: 'border-l-pearl/40',
      iconBg: 'bg-pearl/8',
      textColor: 'text-pearl/50',
      statusIcon: CircleDot,
    }
  }

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const due = new Date(loop.dueAt)
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const weekEnd = new Date(today)
  weekEnd.setDate(weekEnd.getDate() + 7)

  if (due < today) {
    return {
      borderColor: 'border-l-rose',
      iconBg: 'bg-rose/10',
      textColor: 'text-rose-400',
      statusIcon: CircleDot,
    }
  }
  if (due < tomorrow) {
    return {
      borderColor: 'border-l-amber',
      iconBg: 'bg-amber/10',
      textColor: 'text-amber',
      statusIcon: CircleDot,
    }
  }
  if (due < weekEnd) {
    return {
      borderColor: 'border-l-gold',
      iconBg: 'bg-gold/10',
      textColor: 'text-gold-light',
      statusIcon: CircleDot,
    }
  }

  return {
    borderColor: 'border-l-pearl/40',
    iconBg: 'bg-pearl/8',
    textColor: 'text-pearl/50',
    statusIcon: CircleDot,
  }
}

// ─── Due date label ──────────────────────────────────────────
function getDueLabel(loop: Followup, t: (key: string, opts?: Record<string, unknown>) => string): string | null {
  if (loop.status === 'resolved') return null
  if (loop.status === 'snoozed' && loop.snoozedUntil) {
    const d = new Date(loop.snoozedUntil)
    return t('due.snoozedUntil', { date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) })
  }
  if (!loop.dueAt) return null

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const due = new Date(loop.dueAt)
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const diffDays = Math.floor((today.getTime() - due.getTime()) / 86400000)

  if (diffDays > 0) {
    return t('due.overdue', { count: diffDays })
  }
  if (due.toDateString() === today.toDateString()) {
    return t('due.today')
  }
  if (due.toDateString() === tomorrow.toDateString()) {
    return t('due.tomorrow')
  }
  return t('due.weekday', { day: due.toLocaleDateString('en-US', { weekday: 'short' }) })
}

// ─── Source badge ────────────────────────────────────────────
const SOURCE_STYLE: Record<FollowupSource, { icon: LucideIcon; bg: string; color: string }> = {
  email: { icon: Mail, bg: 'bg-gold/10', color: 'text-gold' },
  chat: { icon: MessageSquare, bg: 'bg-pearl/8', color: 'text-pearl/60' },
  manual: { icon: PenLine, bg: 'bg-indigo-400/10', color: 'text-indigo-400' },
}

// ─── Component ───────────────────────────────────────────────
interface Props {
  loop: Followup
  isExpanded: boolean
  onToggle: () => void
  onResolve: (id: string) => void
  onSnooze: (id: string, until: string) => void
  onUpdateDueAt: (id: string, dueAt: string | null) => void
}

export default function LoopCard({ loop, isExpanded, onToggle, onResolve, onSnooze, onUpdateDueAt }: Props) {
  const { t } = useTranslation('followups')
  const meta = getUrgencyMeta(loop)
  const StatusIcon = meta.statusIcon
  const dueLabel = getDueLabel(loop, t)
  const source = SOURCE_STYLE[loop.source]
  const SourceIcon = source.icon
  const isResolved = loop.status === 'resolved'
  const [showSnooze, setShowSnooze] = useState(false)
  const [editingDueAt, setEditingDueAt] = useState(false)
  const [dueAtDraft, setDueAtDraft] = useState('')

  const openDueAtEditor = () => {
    setDueAtDraft(loop.dueAt ? isoToInputDate(loop.dueAt) : '')
    setEditingDueAt(true)
  }

  const closeDueAtEditor = () => {
    setEditingDueAt(false)
    setDueAtDraft('')
  }

  const saveDueAt = () => {
    if (!dueAtDraft) return
    onUpdateDueAt(loop.id, inputDateToIso(dueAtDraft))
    closeDueAtEditor()
  }

  const clearDueAt = () => {
    onUpdateDueAt(loop.id, null)
    closeDueAtEditor()
  }

  const canEditDueAt = loop.status === 'open'

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`glass-card rounded-xl overflow-hidden border border-white/5 border-l-2 ${meta.borderColor} transition-colors hover:border-white/10 ${
        isResolved ? 'opacity-60' : ''
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-4 group"
      >
        <div className="flex items-start gap-3">
          {/* Status icon */}
          <div className={`w-8 h-8 rounded-lg ${meta.iconBg} flex items-center justify-center shrink-0 mt-0.5`}>
            <StatusIcon size={15} className={meta.textColor} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <p className={`text-sm font-medium truncate ${
                isResolved ? 'text-pearl/50 line-through' : 'text-ivory'
              }`}>
                {loop.title}
              </p>
              {dueLabel && (
                <span className={`text-xs whitespace-nowrap shrink-0 ${meta.textColor}`}>
                  {dueLabel}
                </span>
              )}
            </div>

            {/* Source badge */}
            <div className="flex items-center gap-2 mt-1">
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${source.bg} ${source.color}`}>
                <SourceIcon size={10} />
                {t(`source.${loop.source}`)}
              </span>
              {!dueLabel && (
                <span className="text-pearl/55 text-xs">{relativeTime(loop.createdAt)}</span>
              )}
            </div>
          </div>

          {/* Chevron */}
          <AnimatedChevron
            isOpen={isExpanded}
            size={14}
            className="text-pearl/45 group-hover:text-pearl/70 transition-colors mt-1 shrink-0"
          />
        </div>
      </button>

      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            {...expandCollapse}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4">
              <div className="pt-3 border-t border-white/5 space-y-2">
                {loop.context && (
                  <p className="text-pearl/50 text-xs leading-relaxed">{loop.context}</p>
                )}
                <DataRow label={t('detail.created')} value={relativeTime(loop.createdAt)} />
                <DataRow label={t('detail.source')} value={t(`source.${loop.source}`)} />
                {canEditDueAt && (
                  <div>
                    {editingDueAt ? (
                      <div className="flex items-center gap-2 text-xs py-1">
                        <span className="text-pearl/60 min-w-[80px]">{t('detail.dueDate')}</span>
                        <input
                          type="date"
                          value={dueAtDraft}
                          onChange={e => setDueAtDraft(e.target.value)}
                          onClick={e => e.stopPropagation()}
                          autoFocus
                          className="flex-1 px-2 py-1 rounded-md bg-charcoal/50 border border-white/10 text-ivory text-xs focus:outline-none focus:border-gold/40 [color-scheme:dark]"
                        />
                        <button
                          onClick={e => { e.stopPropagation(); saveDueAt() }}
                          disabled={!dueAtDraft}
                          className="px-2 py-1 rounded-md bg-gold/15 text-gold text-xs font-medium hover:bg-gold/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {t('detail.save')}
                        </button>
                        {loop.dueAt && (
                          <button
                            onClick={e => { e.stopPropagation(); clearDueAt() }}
                            className="px-2 py-1 rounded-md bg-rose/10 text-rose-400 text-xs font-medium hover:bg-rose/20 transition-colors"
                          >
                            {t('detail.clearDueDate')}
                          </button>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); closeDueAtEditor() }}
                          className="w-6 h-6 rounded-md bg-slate-mid/40 flex items-center justify-center text-pearl/50 hover:text-ivory transition-colors"
                          aria-label={t('detail.cancel')}
                        >
                          <XIcon size={12} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs py-1">
                        <span className="text-pearl/60 min-w-[80px]">{t('detail.dueDate')}</span>
                        <span className="flex-1 text-ivory">
                          {loop.dueAt
                            ? new Date(loop.dueAt).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                            : <span className="text-pearl/40 italic">{t('detail.noDueDate')}</span>}
                        </span>
                        <button
                          onClick={e => { e.stopPropagation(); openDueAtEditor() }}
                          className="w-6 h-6 rounded-md bg-slate-mid/30 flex items-center justify-center text-pearl/50 hover:text-ivory hover:bg-slate-mid/50 transition-colors"
                          aria-label={loop.dueAt ? t('detail.editDueDate') : t('detail.addDueDate')}
                          title={loop.dueAt ? t('detail.editDueDate') : t('detail.addDueDate')}
                        >
                          {loop.dueAt ? <Pencil size={11} /> : <CalendarPlus size={12} />}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {loop.resolvedAt && (
                  <DataRow label={t('detail.resolved')} value={relativeTime(loop.resolvedAt)} />
                )}
                {loop.snoozedUntil && loop.status === 'snoozed' && (
                  <DataRow label={t('detail.snoozedUntil')} value={new Date(loop.snoozedUntil).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })} />
                )}

                {/* Action buttons */}
                {loop.status !== 'resolved' && (
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); onResolve(loop.id) }}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald/10 text-emerald text-xs font-medium hover:bg-emerald/20 transition-colors"
                    >
                      <Check size={12} />
                      {t('action.resolve')}
                    </button>
                    {loop.status !== 'snoozed' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setShowSnooze(!showSnooze) }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-400/10 text-indigo-400 text-xs font-medium hover:bg-indigo-400/20 transition-colors"
                      >
                        <Clock size={12} />
                        {t('action.snooze')}
                      </button>
                    )}
                  </div>
                )}

                {/* Snooze presets */}
                <AnimatePresence>
                  {showSnooze && (
                    <motion.div {...expandCollapse}>
                      <SnoozeOptions onSnooze={(until) => {
                        setShowSnooze(false)
                        onSnooze(loop.id, until)
                      }} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  )
}
